import { ServerEventValidationSchema } from '@thedealersconcierge/lib/cache/events';
import { createSseParser } from '@thedealersconcierge/lib/cache/sse';
import { QueryClient } from '@tanstack/react-query';
import config from '~/config';
import { buildAuthHeaders } from '~/lib/backend';
import { applyServerEvent } from './applyServerEvent';
import { DealershipScope } from './scope';

/**
 * Open the SSE stream and dispatch each validated event to the cache. Resolves
 * when the stream ends (the caller decides whether to reconnect); rejects on a
 * connection error. Aborting `signal` closes the connection.
 *
 * Events are read defensively: a frame that is not valid JSON or does not match
 * the shared schema is skipped rather than throwing — the stream is lossy.
 */
export const streamServerEvents = async (
  scope: DealershipScope,
  queryClient: QueryClient,
  signal: AbortSignal
): Promise<void> => {
  // We use fetch + a ReadableStream reader rather than the native EventSource
  // API: EventSource cannot set request headers (it only supports cookies via
  // `withCredentials`), but `/events` authenticates with a bearer JWT plus the
  // `DealershipId`/`Application` headers — the same ones every GraphQL call
  // sends via buildAuthHeaders(). fetch lets us attach them, and gives us an
  // AbortSignal for clean teardown. (The generated Zeus SSE client does the same.)
  const headers = new Headers(await buildAuthHeaders());
  headers.set('Accept', 'text/event-stream');

  // Staff stream for the active dealership; the path id is validated server-side
  // against the staff member's authenticated dealership.
  const url = `${config.rawBackendUrl}/events/staff/${scope.dealershipId}`;
  const response = await fetch(url, { method: 'GET', headers, signal });
  if (!response.ok || !response.body) {
    throw new Error(`SSE /events responded ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser();

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      return;
    }

    for (const payload of parser.push(decoder.decode(value, { stream: true }))) {
      let json: unknown;
      try {
        json = JSON.parse(payload);
      } catch {
        continue;
      }

      const parsed = ServerEventValidationSchema.safeParse(json);
      if (parsed.success) {
        applyServerEvent(queryClient, scope, parsed.data);
      }
    }
  }
};
